import { useMemo, useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { Error } from "../../../../../../../models/error";
import { Searches, Filters, Sorts, Sort } from "../../../../../../../models/shipeezi.table";
import { Warehouse } from "../../../../../../../models/warehouse";
import WarehouseService from "../../../../../../../services/warehouse.service";
import { Status } from "../../../../../../../util/enums";
import { WarehouseKeys } from "../../../../../../../util/queries";

const useActions = () => {
  const { currentUser, setHasUserChanged } = useAppState();
  const queryClient = useQueryClient();

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean>();
  const [hasWarehouseCodeError, setHasWarehouseCodeError] = useState<boolean>();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>({ status: [Status.ACTIVE] });
  const [sorting, setSorting] = useState<Sorts>();
  const { t: translate } = useTranslation();

  const attributes = useMemo(
    () => ({
      location: [
        "id",
        "country",
        "suburb",
        "state",
        "longAddress",
        "number",
        "addressLine1",
        "addressLine2",
        "postalcode",
      ],
      branches: ["id", "name", "code"],
      contacts: ["id", "firstName", "email", "mobilePhone", "position"],
      sections: ["id", "name", "allowsItemLoose", "allowsStorageBox", "isDefault"],
      sectionsStorageBoxTypes: ["id", "name"],
      sectionsMaps: ["id", "name", "isActive"],
    }),
    []
  );

  const { refetch, isFetching } = useQuery({
    queryKey: WarehouseKeys.getAll([page, pageSize, filters, searches, sorting]),
    queryFn: () =>
      WarehouseService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        sortBy: sorting
          ? JSON.stringify([...sorting, ["updatedAt", "desc"]])
          : JSON.stringify([["updatedAt", "desc"]]),
        include: JSON.stringify([
          { association: "location", attributes: attributes.location },
          { association: "branches", attributes: attributes.branches },
          { association: "contacts", attributes: attributes.contacts },
          {
            association: "sections",
            attributes: attributes.sections,
            include: [
              { association: "storageBoxTypes", attributes: attributes.sectionsStorageBoxTypes },
              { association: "maps", attributes: attributes.sectionsMaps },
            ],
          },
        ]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    enabled: !!currentUser?.id,
    refetchOnWindowFocus: false,
    onSuccess: (response) => {
      if (response && response.data) setWarehouses(response.data);
    },
  });

  const { refetch: getWarehouseTotal } = useQuery({
    queryKey: WarehouseKeys.count([filters, searches]),
    queryFn: () =>
      WarehouseService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
    onSuccess: (response) => {
      if (response && response.data) setTotal(response.data);
    },
  });

  const { mutate: updateWarehouse, isLoading: isUpdating } = useMutation({
    mutationKey: WarehouseKeys.update(),
    mutationFn: ({ id, warehouse }: { id: number; warehouse: Warehouse }) =>
      WarehouseService.update(id, warehouse),
    onSuccess: () => {
      setHasErrors(false);
      setHasWarehouseCodeError(false);
      refetch();
      getWarehouseTotal();
      setHasUserChanged(true);

      queryClient.invalidateQueries({ queryKey: [WarehouseKeys.all], refetchType: "none" });
    },
    onError: (error: AxiosError<Error>) => {
      if (error && error.response) {
        const { data } = error.response;

        if (data.field === "code" && data.error === "NOT_AVAILABLE") {
          setHasWarehouseCodeError(true);

          return NotificationToast.error(
            translate("warehouseCodeAlreadyExistsPleaseAssignANewCode")
          );
        }
        NotificationToast.error(data?.message);
      }
      setHasErrors(true);
    },
  });

  const { mutate: createWarehouse, isLoading: isCreating } = useMutation({
    mutationKey: WarehouseKeys.create(),
    mutationFn: (warehouse: Warehouse) => WarehouseService.create(warehouse),
    onSuccess: () => {
      setHasErrors(false);
      setHasWarehouseCodeError(false);
      refetch();
      getWarehouseTotal();
      setHasUserChanged(true);

      queryClient.invalidateQueries({ queryKey: [WarehouseKeys.all], refetchType: "none" });
    },
    onError: (error: AxiosError<Error>) => {
      if (error && error.response) {
        const { data } = error.response;

        if (data.field === "code" && data.error === "NOT_AVAILABLE") {
          setHasWarehouseCodeError(true);

          return NotificationToast.error(
            translate("warehouseCodeAlreadyExistsPleaseAssignANewCode")
          );
        }
        NotificationToast.error(data?.message);
      }
      setHasErrors(true);
    },
  });

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleGetDefaultFilters = () => Object.values(filters)?.[0];

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    warehouses,
    page,
    pageSize,
    total,
    hasErrors,
    hasWarehouseCodeError,
    setHasErrors,
    setHasWarehouseCodeError,
    setPageSize,
    setCurrentPage,
    isLoading: isFetching || isCreating || isUpdating,
    handleSearch,
    handleFilter,
    handleSort,
    handleGetDefaultFilters,
    updateWarehouse,
    createWarehouse,
  };
};

export default useActions;
